import type { JsonField, NullableJsonFieldType } from "./model"
import {
  COMMON_KEYWORDS,
  JSON_STRING_FORMATS,
  JsonFieldType,
  NULL,
  TYPE_KEYWORDS,
} from "./model"

export const isNotNullType = <T extends JsonFieldType>(
  value: T | typeof NULL,
): value is T => value !== NULL

export function isFieldOfType<T extends JsonFieldType>(
  field: JsonField,
  type: T,
): field is Extract<JsonField, { type: NullableJsonFieldType<T> }> {
  const t = field.type

  return Array.isArray(t) ? t.some((value) => value === type) : t === type
}

export function isNullable<T extends JsonFieldType>(
  type: NullableJsonFieldType<T>,
): boolean {
  return Array.isArray(type) && type.includes(NULL)
}

export function getBaseType<T extends JsonFieldType>(
  type: NullableJsonFieldType<T>,
): T {
  if (!Array.isArray(type)) {
    return type
  }

  const baseType = type.find(isNotNullType)
  if (!baseType) {
    throw new Error("Nullable JsonFieldType must include a base type")
  }

  return baseType
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

export const isJsonFieldType = (value: unknown): value is JsonFieldType =>
  typeof value === "string" &&
  (Object.values(JsonFieldType) as string[]).includes(value)

export const isNullableType = (
  value: unknown,
): value is [JsonFieldType, typeof NULL] | [typeof NULL, JsonFieldType] =>
  Array.isArray(value) &&
  value.length === 2 &&
  value.includes(NULL) &&
  value.some(isJsonFieldType)

const baseType = (value: JsonField["type"]): JsonFieldType =>
  Array.isArray(value) ? (value.find(isJsonFieldType) as JsonFieldType) : value

/**
 * A single reason a value is not a valid {@link JsonField}, located by JSON pointer
 * (`""` addresses the root). Pointers always terminate at a property name or an object,
 * never at an array element, so that they resolve against a document position.
 */
export type JsonFieldIssue = { pointer: string; message: string }

const escapePointerSegment = (segment: string) =>
  segment.replace(/[/~]/g, (char) => (char === "~" ? "~0" : "~1"))

const childPointer = (pointer: string, ...segments: string[]) =>
  segments.reduce(
    (acc, segment) => `${acc}/${escapePointerSegment(segment)}`,
    pointer,
  )

const issue = (pointer: string, message: string): JsonFieldIssue[] => [
  { pointer, message },
]

const quoted = (values: readonly string[]) =>
  values.map((value) => `\`${value}\``).join(", ")

const optionalNumber = (
  value: unknown,
  pointer: string,
  key: string,
): JsonFieldIssue[] =>
  typeof value === "undefined" || typeof value === "number"
    ? []
    : issue(childPointer(pointer, key), `\`${key}\` must be a number`)

const optionalCount = (
  value: unknown,
  pointer: string,
  key: string,
): JsonFieldIssue[] =>
  typeof value === "undefined" ||
  (typeof value === "number" && Number.isInteger(value) && value >= 0)
    ? []
    : issue(
        childPointer(pointer, key),
        `\`${key}\` must be a non-negative integer`,
      )

const optionalEnum = (
  value: unknown,
  pointer: string,
  isMember: (entry: unknown) => boolean,
  expected: string,
): JsonFieldIssue[] => {
  if (typeof value === "undefined") {
    return []
  }

  const enumPointer = childPointer(pointer, "enum")
  if (!Array.isArray(value)) {
    return issue(enumPointer, "`enum` must be an array")
  }

  // Providers reject an empty or duplicated enum; the visual editor already emits neither
  // (empty becomes undefined, and it dedupes), so this guards only hand-typed code.
  if (!value.length) {
    return issue(enumPointer, "`enum` must list at least one value")
  }

  const wrongType = value.findIndex((entry) => !isMember(entry))
  if (wrongType !== -1) {
    return issue(
      enumPointer,
      `\`enum\` values must be ${expected} (index ${wrongType} is not)`,
    )
  }

  const duplicate = value.findIndex(
    (entry, index) => value.indexOf(entry) !== index,
  )

  return duplicate === -1
    ? []
    : issue(
        enumPointer,
        `\`enum\` values must be unique (index ${duplicate} repeats an earlier value)`,
      )
}

const stringIssues = (
  value: Record<string, unknown>,
  pointer: string,
): JsonFieldIssue[] => [
  ...optionalCount(value.minLength, pointer, "minLength"),
  ...optionalCount(value.maxLength, pointer, "maxLength"),
  ...(typeof value.format === "undefined" ||
  (typeof value.format === "string" &&
    (JSON_STRING_FORMATS as readonly string[]).includes(value.format))
    ? []
    : issue(
        childPointer(pointer, "format"),
        `\`format\` must be one of ${quoted(JSON_STRING_FORMATS)}`,
      )),
  ...optionalEnum(
    value.enum,
    pointer,
    (entry) => typeof entry === "string",
    "strings",
  ),
]

const numericIssues = (
  value: Record<string, unknown>,
  pointer: string,
  isInteger: boolean,
): JsonFieldIssue[] => [
  ...optionalNumber(value.minimum, pointer, "minimum"),
  ...optionalNumber(value.maximum, pointer, "maximum"),
  ...optionalEnum(
    value.enum,
    pointer,
    isInteger ? Number.isInteger : (entry) => typeof entry === "number",
    isInteger ? "integers" : "numbers",
  ),
]

const objectIssues = (
  value: Record<string, unknown>,
  pointer: string,
): JsonFieldIssue[] => {
  const properties = value.properties
  if (!isObject(properties)) {
    return issue(
      childPointer(pointer, "properties"),
      "`properties` must be an object",
    )
  }

  const nested = Object.entries(properties).flatMap(([key, property]) =>
    fieldIssues(property, childPointer(pointer, "properties", key)),
  )

  const additional =
    value.additionalProperties === false
      ? []
      : issue(
          childPointer(pointer, "additionalProperties"),
          "`additionalProperties` must be `false`",
        )

  const required = value.required
  if (
    !Array.isArray(required) ||
    !required.every((entry) => typeof entry === "string")
  ) {
    return [
      ...nested,
      ...additional,
      ...issue(
        childPointer(pointer, "required"),
        "`required` must be an array of property names",
      ),
    ]
  }

  const keys = Object.keys(properties)
  const unknown = required.find((key) => !keys.includes(key))

  return [
    ...nested,
    ...additional,
    ...(unknown === undefined
      ? []
      : issue(
          childPointer(pointer, "required"),
          `\`required\` lists \`${unknown}\`, which is not defined in \`properties\``,
        )),
  ]
}

const arrayIssues = (
  value: Record<string, unknown>,
  pointer: string,
): JsonFieldIssue[] => [
  ...fieldIssues(value.items, childPointer(pointer, "items")),
  ...optionalCount(value.minItems, pointer, "minItems"),
  ...optionalCount(value.maxItems, pointer, "maxItems"),
]

const unsupportedKeywordIssues = (
  value: Record<string, unknown>,
  pointer: string,
  type: JsonFieldType,
): JsonFieldIssue[] => {
  const supported: readonly string[] = [
    ...COMMON_KEYWORDS,
    ...TYPE_KEYWORDS[type],
  ]

  return Object.keys(value)
    .filter((keyword) => !supported.includes(keyword))
    .map((keyword) => ({
      pointer: childPointer(pointer, keyword),
      message: `\`${keyword}\` is not supported on a \`${type}\` field`,
    }))
}

function fieldIssues(value: unknown, pointer: string): JsonFieldIssue[] {
  if (!isObject(value)) {
    return issue(pointer, "A field must be a JSON object")
  }

  const type = value.type
  if (!isJsonFieldType(type) && !isNullableType(type)) {
    return issue(
      childPointer(pointer, "type"),
      `\`type\` must be one of ${quoted(Object.values(JsonFieldType))}, optionally paired with \`${NULL}\``,
    )
  }

  const resolvedType = baseType(type)

  const sharedIssues = [
    ...(typeof value.description === "undefined" ||
    typeof value.description === "string"
      ? []
      : issue(
          childPointer(pointer, "description"),
          "`description` must be a string",
        )),
    ...unsupportedKeywordIssues(value, pointer, resolvedType),
  ]

  switch (resolvedType) {
    case JsonFieldType.string:
      return [...sharedIssues, ...stringIssues(value, pointer)]
    case JsonFieldType.number:
      return [...sharedIssues, ...numericIssues(value, pointer, false)]
    case JsonFieldType.integer:
      return [...sharedIssues, ...numericIssues(value, pointer, true)]
    case JsonFieldType.boolean:
      return sharedIssues
    case JsonFieldType.object:
      return [...sharedIssues, ...objectIssues(value, pointer)]
    case JsonFieldType.array:
      return [...sharedIssues, ...arrayIssues(value, pointer)]
  }
}

/**
 * The single authority on what the editor accepts: it both gates whether a code edit
 * reaches the form and, via {@link JsonFieldIssue}, drives the editor's lint markers.
 */
export const validateJsonField = (value: unknown): JsonFieldIssue[] =>
  fieldIssues(value, "")

export const isJsonField = (value: unknown): value is JsonField =>
  validateJsonField(value).length === 0

const getBaseTypeFromUnknown = (value: unknown): JsonFieldType | undefined => {
  if (isJsonFieldType(value)) {
    return value
  }
  if (Array.isArray(value)) {
    return value.find(isJsonFieldType)
  }

  return undefined
}

export const normalizeJsonFieldShape = (value: unknown): void => {
  if (!isObject(value)) {
    return
  }

  const baseType = getBaseTypeFromUnknown(value.type)
  if (!baseType) {
    return
  }

  if (baseType === JsonFieldType.array) {
    if (!isObject(value.items)) {
      value.items = { type: JsonFieldType.string }
    }
    normalizeJsonFieldShape(value.items)
  }

  if (baseType === JsonFieldType.object) {
    if (!isObject(value.properties)) {
      value.properties = {}
    }
    if (!Array.isArray(value.required)) {
      value.required = []
    }
    if (value.additionalProperties !== false) {
      value.additionalProperties = false
    }

    const props = value.properties as Record<string, unknown>

    value.required = Object.keys(props)

    Object.values(props).forEach((prop) => {
      normalizeJsonFieldShape(prop)
    })
  }
}

export const stringifySchema = (schema: JsonField) =>
  JSON.stringify(schema, null, 2)
